import { useState } from "react";
import axios from "axios";
import { API } from "../../App";
import { KeyRound, Check, Eye, EyeOff } from "lucide-react";

export default function ChangePasswordModal({ onClose, t }) {
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (newPassword.length < 6) {
      setError(t('password_min_6'));
      return;
    }
    if (newPassword !== confirmPassword) {
      setError(t('passwords_not_match'));
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/auth/change-password`, {
        current_password: currentPassword,
        new_password: newPassword
      });
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.detail || t('could_not_change_password'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-3xl p-6 w-full max-w-sm shadow-2xl">
        <div className="text-center mb-6">
          <div className="bg-[#118AB2]/10 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
            <KeyRound size={32} color="#118AB2" />
          </div>
          <h2 className="text-2xl font-black text-[#073B4C]" style={{ fontFamily: "Fredoka, sans-serif" }}>
            {success ? t('password_changed') : t('change_password')}
          </h2>
          <p className="text-gray-500 text-sm mt-1">
            {success ? t('password_updated_success') : t('enter_current_new_password')}
          </p>
        </div>

        {success ? (
          <button
            onClick={onClose}
            className="w-full bg-[#06D6A0] text-white font-black py-3 rounded-xl hover:bg-[#05b588] transition-colors"
            style={{ fontFamily: "Fredoka, sans-serif" }}
          >
            <Check size={20} className="inline mr-2" />
            {t('done')}
          </button>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-1">{t('current_password')}</label>
              <div className="relative">
                <input
                  type={showCurrent ? "text" : "password"}
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  className="w-full border-2 border-gray-200 rounded-xl px-4 py-3 pr-12 focus:outline-none focus:border-[#118AB2] transition-colors text-gray-800 font-medium"
                  placeholder={t('enter_current_password')}
                  required
                  data-testid="current-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowCurrent(!showCurrent)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showCurrent ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>

            <div>
              <label className="block text-sm font-bold text-gray-700 mb-1">{t('new_password')}</label>
              <div className="relative">
                <input
                  type={showNew ? "text" : "password"}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="w-full border-2 border-gray-200 rounded-xl px-4 py-3 pr-12 focus:outline-none focus:border-[#118AB2] transition-colors text-gray-800 font-medium"
                  placeholder={t('enter_new_password')}
                  required
                  minLength={6}
                  data-testid="new-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowNew(!showNew)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showNew ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>

            <div>
              <label className="block text-sm font-bold text-gray-700 mb-1">{t('confirm_new_password')}</label>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="w-full border-2 border-gray-200 rounded-xl px-4 py-3 focus:outline-none focus:border-[#118AB2] transition-colors text-gray-800 font-medium"
                placeholder={t('confirm_new_password')}
                required
                minLength={6}
                data-testid="confirm-password-input"
              />
            </div>

            {error && (
              <div className="bg-red-50 border-2 border-red-200 rounded-xl px-4 py-3">
                <p className="text-red-600 text-sm font-medium">{error}</p>
              </div>
            )}

            <div className="flex gap-3 pt-2">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-600 font-bold py-3 rounded-xl transition-all"
              >
                {t('cancel')}
              </button>
              <button
                type="submit"
                disabled={loading}
                className="flex-1 bg-[#118AB2] hover:bg-[#0d7a9e] text-white font-black py-3 rounded-xl transition-all disabled:opacity-60"
                style={{ fontFamily: "Fredoka, sans-serif" }}
                data-testid="submit-change-password"
              >
                {loading ? t('changing') : t('change_password')}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
